import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { RevealText } from "./Reveal";
import { TID } from "@/lib/testIds";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const titleY = useTransform(scrollYProgress, [0, 1], ["0%", "-15%"]);

  return (
    <section
      ref={ref}
      data-testid={TID.hero.root}
      className="relative min-h-[100vh] w-full overflow-hidden"
    >
      {/* Ruled grid background */}
      <div className="absolute inset-0 grid grid-cols-12 pointer-events-none">
        {Array.from({ length: 13 }).map((_, i) => (
          <div key={i} className="border-l border-[#D9D6D0]/60 h-full" />
        ))}
      </div>

      <div className="relative max-w-[1600px] mx-auto px-6 lg:px-12 pt-16 md:pt-24 pb-24">
        {/* Meta bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.1 }}
          className="flex flex-wrap items-center justify-between gap-4 mb-16 md:mb-24 border-b border-[#D9D6D0] pb-6"
        >
          <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#111111]/60">
            Vol. XVIII · Est. 2007 · Halifax, Nova Scotia
          </p>
          <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#111111]/60">
            44.6488° N · 63.5752° W
          </p>
          <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#C17A43]">
            ● On duty · 24 / 7 / 365
          </p>
        </motion.div>

        {/* Kinetic headline */}
        <motion.div style={{ y: titleY }} className="grid grid-cols-12 gap-4 items-end">
          <div className="col-span-12 lg:col-span-8">
            <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#C17A43] mb-6 md:mb-10">
              — Chapter 00 · Standing Watch Since 2007
            </p>

            <h1
              data-testid={TID.hero.line}
              className="font-serif text-[clamp(2.5rem,8.5vw,9rem)] leading-[0.92] tracking-tighter text-[#111111]"
            >
              <RevealText text="Security guard" delay={0.1} className="block" />
              <RevealText
                text="services"
                delay={0.35}
                className="block italic text-[#184C59]"
              />
              <RevealText text="in Nova Scotia." delay={0.6} className="block" />
            </h1>
          </div>

          <div className="col-span-12 lg:col-span-4 lg:pb-4">
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.1, ease: [0.16, 1, 0.3, 1] }}
              className="font-sans text-base md:text-lg leading-relaxed text-[#111111]/80 max-w-[36ch]"
            >
              Plan coverage around your building, port-related property, event
              or industrial site. Ask about guard duties, shift times and local
              availability.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.35 }}
              className="mt-8 flex flex-wrap items-center gap-4"
            >
              <Link
                to="/services"
                data-testid={TID.hero.cta}
                className="group inline-flex items-center gap-3 bg-[#111111] text-[#F5F3EF] px-6 py-4 font-mono text-[11px] uppercase tracking-[0.24em] hover:bg-[#C17A43] transition-colors duration-300"
              >
                Explore Services
                <span className="inline-block transition-transform duration-300 group-hover:translate-x-1">→</span>
              </Link>
              <Link
                to="/coverage"
                className="font-mono text-[11px] uppercase tracking-[0.24em] text-[#111111] border-b border-[#111111] pb-0.5 hover:text-[#C17A43] hover:border-[#C17A43] transition-colors"
              >
                Coverage Map
              </Link>
            </motion.div>
          </div>
        </motion.div>

        {/* Framed photograph */}
        <div className="relative mt-20 md:mt-28 grid grid-cols-12 gap-4">
          <div className="col-span-12 lg:col-span-8 relative overflow-hidden aspect-[16/9] border border-[#111111]/10">
            <motion.img
              style={{ y: imgY, scale: imgScale }}
              src="https://images.unsplash.com/photo-1566245024852-04fbf7842ce9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwxfHxzZWN1cml0eSUyMGd1YXJkJTIwcGF0cm9sJTIwZm9nfGVufDB8fHx8MTc4NTEwNTY3Mnww&ixlib=rb-4.1.0&q=85"
              alt="Nova Scotia Security Services officer on patrol in coastal fog"
              className="absolute inset-0 w-full h-[130%] object-cover grayscale contrast-110"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#111111]/50 via-transparent to-transparent" />
            <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between text-[#F5F3EF]">
              <p className="font-mono text-[10px] uppercase tracking-[0.28em]">
                Fig. 01 · Overnight patrol · Halifax Seaport
              </p>
              <p className="font-mono text-[10px] uppercase tracking-[0.28em]">
                03:42 AST
              </p>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="col-span-12 lg:col-span-4 flex flex-col justify-between border border-[#D9D6D0] p-8 bg-[#F5F3EF]"
          >
            <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#C17A43]">
              — Facts of Record
            </p>
            <div className="grid grid-cols-2 gap-6 mt-8">
              <Stat label="Years operating" value="18" />
              <Stat label="Sites secured" value="340" />
              <Stat label="Guards on roster" value="210" />
              <Stat label="Cities covered" value="63" />
            </div>
            <p className="mt-8 font-sans text-sm text-[#111111]/70 leading-relaxed">
              A licensed Nova Scotia security company — bonded, insured, and
              audited annually.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

const Stat = ({ label, value }) => (
  <div>
    <p className="font-serif text-4xl md:text-5xl leading-none text-[#111111]">{value}</p>
    <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.24em] text-[#111111]/60">
      {label}
    </p>
  </div>
);
